import React, { useContext, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import ListItemButton from "@mui/material/ListItemButton";
import Typography from "@mui/material/Typography";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import MovieRoundedIcon from "@mui/icons-material/MovieRounded";
import PlaylistPlayRoundedIcon from "@mui/icons-material/PlaylistPlayRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";

import { DatabaseContext, UserContext } from "../../context";
import firebase from "../../firebase";
import { makeid } from "../../utility";
import DialogWithTextInput from "../DialogWithTextInput";
import {
    buildLibrarySearchIndex,
    buildPlaylistsAndTimelinesLibrary,
} from "./PlaylistsAndTimelinesPage";

function formatLastOpened(value) {
    if (!value) return null;
    const parsed = dayjs(value);
    return parsed.isValid()
        ? `Opened ${parsed.format("D MMM YYYY, HH:mm")}`
        : null;
}

export function routeForPlaylistOrTimeline(item) {
    return item.kind === "playlist"
        ? `/playlist?key=${item.key}`
        : `/analysis?video=${item.video}&key=${item.key}`;
}

function CollectionRow({ item, onClick }) {
    const isPlaylist = item.kind === "playlist";
    const lastOpened = formatLastOpened(item.lastOpened);

    return (
        <ListItemButton
            onClick={onClick}
            sx={{
                width: "100%",
                minHeight: 60,
                my: 0.625,
                px: 1.25,
                py: 1,
                color: "text.primary",
                backgroundColor: "background.subtle",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2.5,
                boxSizing: "border-box",
                transition:
                    "background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease",
                "& .MuiTouchRipple-rippleVisible": {
                    opacity: "0.12 !important",
                    animationDuration: "300ms !important",
                },
                "&:hover": {
                    backgroundColor: "background.media",
                    borderColor: "text.disabled",
                    boxShadow: "0 3px 10px rgba(0, 0, 0, 0.14)",
                },
                "&:active": {
                    transform: "translateY(0.5px)",
                },
                "&:focus-visible": {
                    outline: "3px solid rgba(0, 149, 61, 0.28)",
                    outlineOffset: 1,
                },
                "@media (prefers-reduced-motion: reduce)": {
                    transition: "none",
                    "&:active": { transform: "none" },
                },
            }}
        >
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    color: "text.secondary",
                    backgroundColor: "action.hover",
                    borderRadius: 2,
                }}
            >
                {isPlaylist ? (
                    <PlaylistPlayRoundedIcon />
                ) : (
                    <TimelineRoundedIcon />
                )}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1, ml: 1.5 }}>
                <Typography
                    component="span"
                    sx={{
                        display: "-webkit-box",
                        color: "inherit",
                        fontSize: 16,
                        lineHeight: 1.2,
                        overflowWrap: "break-word",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {item.name || `Untitled ${item.kind}`}
                </Typography>
                <Box
                    sx={{
                        minWidth: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.875,
                        mt: 0.5,
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            flexShrink: 0,
                            px: 0.875,
                            py: 0.25,
                            color: item.isOwner ? "primary.main" : "info.main",
                            backgroundColor: item.isOwner
                                ? "rgba(0, 149, 61, 0.1)"
                                : "rgba(48, 100, 158, 0.09)",
                            border: "1px solid",
                            borderColor: item.isOwner
                                ? "rgba(0, 149, 61, 0.16)"
                                : "rgba(48, 100, 158, 0.15)",
                            borderRadius: 999,
                            fontSize: 10,
                            fontWeight: 800,
                            letterSpacing: "0.045em",
                            lineHeight: 1.2,
                            textTransform: "uppercase",
                        }}
                    >
                        {item.isOwner
                            ? "Yours"
                            : item.creatorName
                              ? `Created by ${item.creatorName}`
                              : "Created by unknown user"}
                    </Box>
                    {lastOpened && (
                        <Typography
                            component="span"
                            sx={{
                                minWidth: 0,
                                color: "text.secondary",
                                fontSize: 12,
                                lineHeight: 1.15,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {lastOpened}
                        </Typography>
                    )}
                </Box>
            </Box>
        </ListItemButton>
    );
}

function Section({ label, items, onItemSelect }) {
    if (items.length === 0) return null;

    return (
        <Box>
            <Typography
                component="h3"
                sx={{
                    m: 0,
                    px: 1.75,
                    pt: 1.625,
                    pb: 0.375,
                    color: "text.secondary",
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                }}
            >
                {label}
            </Typography>
            <Box sx={{ px: 0.625 }}>
                {items.map((item) => (
                    <CollectionRow
                        key={`${item.kind}-${item.key}`}
                        item={item}
                        onClick={() => onItemSelect(item)}
                    />
                ))}
            </Box>
        </Box>
    );
}

export default function VideoPlaylistsAndTimelinesDialog({
    open,
    onClose,
    video,
    canCreateTimeline = false,
}) {
    const history = useHistory();
    const { currentUserData } = useContext(UserContext);
    const [newTimelineNameDialogIsOpen, setNewTimelineNameDialogIsOpen] =
        useState(false);
    const {
        files,
        filesLoaded,
        folders,
        foldersLoaded,
        playlistInfo,
        playlistInfoLoaded,
        playlistUsage,
        playlistUsageLoaded,
        timelineInfo,
        timelineInfoLoaded,
        timelineUsage,
        timelineUsageLoaded,
        groups,
        groupsLoaded,
        users,
    } = useContext(DatabaseContext);

    const isLoading =
        !currentUserData ||
        !filesLoaded ||
        !foldersLoaded ||
        !playlistInfoLoaded ||
        !playlistUsageLoaded ||
        !timelineInfoLoaded ||
        !timelineUsageLoaded ||
        !groupsLoaded;

    const linkedItems = useMemo(() => {
        if (isLoading || !video) return [];

        const library = buildPlaylistsAndTimelinesLibrary({
            files: files || [],
            folders: folders || [],
            playlistInfo: playlistInfo || [],
            playlistUsage: playlistUsage || [],
            timelineInfo: timelineInfo || [],
            timelineUsage: timelineUsage || [],
            groups: groups || [],
            users: users || [],
            currentUserData,
        });
        const videoResult = buildLibrarySearchIndex(library.root).videos.find(
            (result) => result.key === video.key,
        );

        return videoResult?.node.linkedItems || [];
    }, [
        currentUserData,
        files,
        folders,
        groups,
        isLoading,
        playlistInfo,
        playlistUsage,
        timelineInfo,
        timelineUsage,
        users,
        video,
    ]);

    const playlists = linkedItems.filter((item) => item.kind === "playlist");
    const timelines = linkedItems.filter((item) => item.kind === "timeline");

    function handleItemSelect(item) {
        onClose();
        history.push(routeForPlaylistOrTimeline(item));
    }

    function handleContextMenu(event) {
        event.preventDefault();
        event.stopPropagation();
    }

    function handleCloseNewTimelineNameDialog(name, okPressed) {
        if (
            canCreateTimeline &&
            name !== "" &&
            okPressed &&
            video &&
            currentUserData
        ) {
            firebase.database().ref("fileusage").push({
                file: video.key,
                fileName: video.displayName,
                fileType: video.type,
                user: currentUserData.key,
                action: "TimelineCreate",
                date: dayjs().toString(),
            });

            const key = makeid(19);
            const newTimeline = {
                name,
                owner: currentUserData.key,
                video: video.key,
                created: dayjs().toString(),
            };

            firebase.database().ref("timelines").child(key).set(newTimeline);
            firebase
                .database()
                .ref("timelineinfo")
                .child(key)
                .set(newTimeline);
        }

        setNewTimelineNameDialogIsOpen(false);
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                onContextMenu={handleContextMenu}
                aria-labelledby="video-playlists-and-timelines-title"
                fullWidth
                maxWidth="sm"
                slotProps={{
                    paper: {
                        onContextMenu: handleContextMenu,
                        sx: {
                            borderRadius: 3,
                            overflow: "hidden",
                        },
                    },
                }}
            >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Avatar
                    variant="rounded"
                    sx={{
                        width: 44,
                        height: 44,
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: "action.hover",
                        borderRadius: 2,
                    }}
                >
                    <MovieRoundedIcon />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        id="video-playlists-and-timelines-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        Playlists and Timelines
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            mt: 0.5,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {video?.displayName || "Video"}
                    </Typography>
                </Box>
            </Box>

            <DialogContent
                dividers
                sx={{
                    minHeight: 220,
                    maxHeight: "min(62dvh, 620px)",
                    px: { xs: 1, sm: 2 },
                    py: 1,
                    backgroundColor: "background.default",
                }}
            >
                {isLoading ? (
                    <Box
                        role="status"
                        aria-label="Loading playlists and timelines"
                        sx={{
                            minHeight: 200,
                            display: "grid",
                            placeItems: "center",
                        }}
                    >
                        <CircularProgress size={30} />
                    </Box>
                ) : linkedItems.length === 0 ? (
                    <Box
                        sx={{
                            minHeight: 200,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 3,
                            color: "text.secondary",
                            textAlign: "center",
                        }}
                    >
                        <PlaylistPlayRoundedIcon
                            sx={{ mb: 1.5, color: "text.disabled", fontSize: 42 }}
                        />
                        <Typography variant="body2">
                            No playlists or timelines are associated with this
                            video yet.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Section
                            label="Playlists"
                            items={playlists}
                            onItemSelect={handleItemSelect}
                        />
                        <Section
                            label="Timelines"
                            items={timelines}
                            onItemSelect={handleItemSelect}
                        />
                    </>
                )}
            </DialogContent>

                <DialogActions
                    sx={{ px: { xs: 2, sm: 3 }, py: 1.5, gap: 1 }}
                >
                    <Button onClick={onClose} color="inherit">
                        Close
                    </Button>
                    {canCreateTimeline && (
                        <Button
                            variant="contained"
                            disableElevation
                            startIcon={<AddRoundedIcon />}
                            onClick={() =>
                                setNewTimelineNameDialogIsOpen(true)
                            }
                        >
                            New timeline
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {canCreateTimeline && (
                <DialogWithTextInput
                    open={newTimelineNameDialogIsOpen}
                    onClose={handleCloseNewTimelineNameDialog}
                    title="New timeline"
                    message="Enter a name for the new timeline."
                    confirmLabel="Create timeline"
                    modern
                />
            )}
        </>
    );
}
